// React
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Redux
import { useDispatch, useSelector } from "react-redux";
import { selectPatient, setPatient } from "Store/PatientSlice";
import { selectClient, selectClinics } from "Store/ClientSlice";
import { openSnackbar } from "Store/SnackbarSlice";

// MUI
import {
  Typography,
  CircularProgress,
  Dialog,
  DialogContent,
  Box,
  IconButton,
  Stack,
} from "@mui/material";
import {
  MedicalInformationOutlined,
  ChevronRightOutlined,
  ChevronLeftOutlined,
  CloseOutlined,
} from "@mui/icons-material";

// Styles
import { styles } from "./styles";
import { white } from "Constants/ColourConstants";

// Components
import { MNButton } from "Common/Components";
import { LocationStep, DetailsStep, PreferredLanguageStep } from "./steps";
import PortalLoading from "Common/Components/PortalLoading";
import { MBSPatientInfoHeader } from "Components/Users/MBS/Common";

// Resources
import { sortReasons } from "Utils/Reasons";
import { updatePatient } from "API/Resource/patient";
import {
  fetchAppointments,
  getTranslatedReason,
  getReasonTypesByLocation,
} from "Components/Appointments/Common";

// Constants
import { MAX_DETAIL_CHARACTERS } from "Constants/ReasonForVisitConstants";

const ReasonForVisitModal = ({
  open = true,
  isMBS = false,
  patient: patientProp,
  client: clientProp,
  clinics: clinicsProp,
  setOpenReasonForVisit,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const reduxPatient = useSelector(selectPatient);
  const reduxClient = useSelector(selectClient);
  const reduxClinics = useSelector(selectClinics);

  const patient = patientProp || reduxPatient;
  const client = useMemo(
    () => clientProp || reduxClient || {},
    [clientProp, reduxClient]
  );
  const loadingClient = Object.keys(client).length === 0;

  const [stateData, setStateData] = useState({
    location: "",
    locationInfo: null,
    reasonForVisit: "",
    additionalInfo: "",
    availableSlots: [],
    preferredLanguage: patient?.preferredLanguage || "",
  });
  const [submit, setSubmit] = useState(false);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [languageUpdating, setLanguageUpdating] = useState(false);
  const [uniqueTypes, setUniqueTypes] = useState([]);

  const showLocationStep = client?.settings?.appointmentLocations;
  const showReasonStep = client?.settings?.appointmentReasons;
  const showLanguageStep = !patient?.preferredLanguage;
  const totalSteps = showLanguageStep ? 3 : 2;

  // >= for the case where language step is skipped after failed appointment fetch, but language was updated
  const isLastStep = step >= totalSteps;

  const errorMessage = useRef("");
  // prevent re-calling API when we don't have to
  const lastFetchedCriteria = useRef({ location: null, reasonForVisit: null });
  const lastPatientLanguageUpdate = useRef(null);

  const clinics = useMemo(() => {
    return clinicsProp || reduxClinics || [];
  }, [clinicsProp, reduxClinics]);

  useEffect(() => {
    const reasonTypes = getReasonTypesByLocation(clinics, stateData.location);
    setUniqueTypes(reasonTypes);
  }, [clinics, stateData.location]);

  useEffect(() => {
    const lang = patient?.preferredLanguage || "";
    setStateData((prev) => ({ ...prev, preferredLanguage: lang }));
  }, [patient]);

  const sortedUniqueTypes = useMemo(
    () =>
      sortReasons({
        reasons: uniqueTypes,
        // need to sort based in english because the reason types are in english in the DB
        words:
          t("highlightWords.sorting", { lng: "en", returnObjects: true }) || [],
      }),
    [uniqueTypes, t]
  );

  const hideReasonStep = useMemo(() => {
    const reasonTypes = getReasonTypesByLocation(clinics, stateData.location);
    return reasonTypes.length <= 1;
  }, [clinics, stateData.location]);

  useEffect(() => {
    if (!(loading || languageUpdating) && submit) {
      if (errorMessage.current) {
        dispatch(
          openSnackbar({ message: errorMessage.current, severity: "error" })
        );
        setSubmit(false);
        return;
      }

      if (isMBS) {
        navigate("/mbs/appointments", {
          state: { ...stateData, patient, client },
        });
      } else {
        navigate("/Appointments", { state: stateData });
      }
    }
  }, [
    isMBS,
    loading,
    languageUpdating,
    submit,
    stateData,
    errorMessage,
    navigate,
    patient,
    client,
    dispatch,
  ]);

  const updateStateData = (key, value) => {
    setStateData((prev) => ({ ...prev, [key]: value }));
  };

  const handleLocationChange = (event) => {
    const selected = client?.appointmentLocations?.find(
      (item) => item.location.en === event.target.value
    );

    setStateData((prev) => ({
      ...prev,
      location: selected?.location.en || "",
      locationInfo: selected?.information || null,
      reasonForVisit: "",
    }));
  };

  const handleDetailsChange = (event) => {
    const nextValue = event.target.value.slice(0, MAX_DETAIL_CHARACTERS);
    updateStateData("additionalInfo", nextValue);
  };

  const fetchCheckRequest = async () => {
    const handleFetchAppointments = async () => {
      try {
        setLoading(true);
        const appointments = await fetchAppointments(
          patient,
          stateData.reasonForVisit,
          stateData.location,
          null,
          isMBS && client.id ? client.id : null
        );
        updateStateData("availableSlots", appointments);
      } catch (error) {
        errorMessage.current = t("errors.errorFetchingAppointments");
      } finally {
        setLoading(false);
      }
    };

    const hasFetchCriteriaChanged =
      lastFetchedCriteria.current.location !== stateData.location ||
      lastFetchedCriteria.current.reasonForVisit !== stateData.reasonForVisit;

    if (!hasFetchCriteriaChanged) {
      return;
    }

    await handleFetchAppointments();

    if (!errorMessage.current) {
      lastFetchedCriteria.current = {
        location: stateData.location,
        reasonForVisit: stateData.reasonForVisit,
      };
    }
  };

  const handleLanguageUpdate = async () => {
    if (
      showLanguageStep &&
      patient?.id &&
      stateData.preferredLanguage &&
      stateData.preferredLanguage !== patient?.preferredLanguage &&
      lastPatientLanguageUpdate.current !== stateData.preferredLanguage
    ) {
      try {
        setLanguageUpdating(true);
        const payload = {
          ...patient,
          preferredLanguage: stateData.preferredLanguage,
        };
        await updatePatient(payload);

        if (!patientProp) {
          dispatch(setPatient(payload));
        }
        lastPatientLanguageUpdate.current = stateData.preferredLanguage;
      } catch (error) {
        errorMessage.current = t("reasonForVisitModal.languageUpdateError");
      } finally {
        setLanguageUpdating(false);
      }
    }
  };

  const handleSubmit = () => {
    if (showLanguageStep && !stateData.preferredLanguage) {
      return;
    }

    errorMessage.current = "";
    handleLanguageUpdate();
    fetchCheckRequest();
    setSubmit(true);
  };

  const locationOptions =
    client?.appointmentLocations?.slice().sort((a, b) => {
      const nameA = (a.location[i18n.language] || a.location.en).toLowerCase();
      const nameB = (b.location[i18n.language] || b.location.en).toLowerCase();

      return nameA.localeCompare(nameB, i18n.language, {
        sensitivity: "base",
      });
    }) || [];

  const selectedReasonLabel =
    getTranslatedReason(
      stateData.reasonForVisit,
      client?.appointmentReasons || [],
      i18n.language
    ) || stateData.reasonForVisit;

  const handleNext = async () => {
    if (step === 1) {
      setStep(2);
      fetchCheckRequest();
    } else if (step === 2 && showLanguageStep) {
      setStep(3);
    }
  };

  const handleBack = () => {
    if (step === 3) {
      setStep(2);
      return;
    }

    setStep(1);
  };

  const disableNext =
    loadingClient ||
    (step === 1 &&
      ((showLocationStep && !stateData.location) ||
        (step === 1 &&
          Boolean(stateData.location) &&
          showReasonStep &&
          !hideReasonStep &&
          !stateData.reasonForVisit))) ||
    (step === 2 && showLanguageStep && !stateData.additionalInfo?.trim());

  const submissionInProgress = submit || languageUpdating;
  const missingInfo =
    !stateData.additionalInfo?.trim() ||
    (showLanguageStep && !stateData.preferredLanguage);

  return (
    <Dialog
      open={open}
      onClose={() => !isMBS && setOpenReasonForVisit(false)}
      fullWidth
      aria-labelledby="reason-for-visit-dialog"
      maxWidth="md"
      slotProps={{
        paper: {
          sx: {
            p: 3,
            m: 0,
            position: "relative",
          },
        },
      }}
      sx={{ minWidth: "300px" }}
    >
      <DialogContent sx={styles.dialogContent} id="reason-for-visit-dialog">
        {loadingClient && (
          <PortalLoading message={t("common.loading")} minHeight="auto" />
        )}

        {!loadingClient && (
          <>
            <Box sx={styles.titleRow}>
              <MedicalInformationOutlined sx={styles.titleIcon} />
              <Typography variant={isMBS ? "h2" : "h5"} sx={styles.titleText}>
                {t("reasonForVisitModal.appointmentInformation")}
              </Typography>

              <IconButton
                aria-label={t("buttons.cancel")}
                sx={styles.mobileCloseButton}
                onClick={() => setOpenReasonForVisit(false)}
              >
                <CloseOutlined />
              </IconButton>
            </Box>
            {isMBS && <MBSPatientInfoHeader patient={patient} />}
            {step === 1 && (
              <LocationStep
                showLocationStep={showLocationStep}
                showReasonStep={showReasonStep}
                hideReasonStep={hideReasonStep}
                location={stateData.location}
                reasonForVisit={stateData.reasonForVisit}
                locationOptions={locationOptions}
                sortedUniqueTypes={sortedUniqueTypes}
                appointmentReasons={client?.appointmentReasons || []}
                onLocationChange={handleLocationChange}
                onReasonForVisitChange={(value) =>
                  updateStateData("reasonForVisit", value)
                }
              />
            )}

            {step === 2 && (
              <DetailsStep
                isMBS={isMBS}
                location={stateData.location}
                selectedReasonLabel={selectedReasonLabel}
                additionalInfo={stateData.additionalInfo}
                submit={submit}
                onDetailsChange={handleDetailsChange}
              />
            )}

            {step === 3 && (
              <PreferredLanguageStep
                preferredLanguage={stateData.preferredLanguage}
                onPreferredLanguageChange={(value) =>
                  updateStateData("preferredLanguage", value)
                }
              />
            )}
          </>
        )}
      </DialogContent>

      <Stack sx={styles.actionsContainer}>
        <MNButton
          onClick={() => setOpenReasonForVisit(false)}
          variant="text"
          ariaLabel="buttons.cancel"
          sx={styles.cancelTextButton}
        >
          {t("buttons.cancel")}
        </MNButton>

        {step > 1 && (
          <MNButton
            ariaLabel="buttons.back"
            onClick={handleBack}
            disabled={submissionInProgress}
            variant="outlined"
            sx={styles.actionButton}
            startIcon={<ChevronLeftOutlined />}
          >
            {t("buttons.back")}
          </MNButton>
        )}
        {!isLastStep && !submit ? (
          <MNButton
            ariaLabel="buttons.next"
            onClick={handleNext}
            disabled={disableNext}
            variant="contained"
            sx={styles.actionButton}
            endIcon={<ChevronRightOutlined />}
          >
            {t("buttons.next")}
          </MNButton>
        ) : (
          <MNButton
            ariaLabel="reasonForVisitModal.seeAppointments"
            onClick={handleSubmit}
            disabled={submissionInProgress || missingInfo}
            variant="contained"
            sx={styles.actionButton}
            endIcon={<ChevronRightOutlined />}
            startIcon={
              submissionInProgress && (
                <CircularProgress size={14} sx={{ color: white, mr: 1 }} />
              )
            }
          >
            {submit
              ? t("reasonForVisitModal.searching")
              : t("reasonForVisitModal.seeAppointments")}
          </MNButton>
        )}
      </Stack>
    </Dialog>
  );
};

export default ReasonForVisitModal;
